// Phase 9 — Settings opens as a right-side Sheet so the dashboard,
// sidebar, websocket connections, and React Query cache all stay alive
// underneath. The legacy /settings route still works (deep links + bookmarks).
//
// We lazy-load the Settings page so its 530-line component isn't paid
// for until the user actually opens the sheet.

import { Suspense, lazy, startTransition, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Loader2 } from 'lucide-react';

const Settings = lazy(() => import('@/pages/Settings'));

// We open/close via a search-param so the state is URL-shareable and
// survives a refresh (`?settings=1`). The Sidebar trigger sets the param
// and this component reads it.
export function SettingsSlideover() {
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const open = params.get('settings') === '1';

  // Disable scroll on the background while the sheet is open.
  useEffect(() => {
    if (open) document.body.classList.add('overflow-hidden');
    else document.body.classList.remove('overflow-hidden');
    return () => document.body.classList.remove('overflow-hidden');
  }, [open]);

  const setOpen = (next: boolean) => {
    const p = new URLSearchParams(location.search);
    if (next) p.set('settings', '1');
    else      p.delete('settings');
    const q = p.toString();
    // Phase 12.3 P0 — startTransition for the same reason as the
    // sidebar opener: opening the sheet mounts a lazy <Settings/>
    // inside <Suspense>, and a sync URL change triggers React 426.
    // Closing the sheet doesn't suspend, but wrapping both keeps the
    // navigate cohesive (no flicker when toggling rapidly).
    startTransition(() => {
      navigate({ pathname: location.pathname, search: q ? `?${q}` : '' }, { replace: false });
    });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Settings</SheetTitle>
          <SheetDescription>Organization configuration. Dashboard stays live behind this panel.</SheetDescription>
        </SheetHeader>
        <div className="mt-4">
          <Suspense fallback={
            <div className="flex items-center justify-center min-h-[40vh]">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          }>
            <Settings />
          </Suspense>
        </div>
      </SheetContent>
    </Sheet>
  );
}
